import { cellRenderer, customHeader } from "../SAR/SARGrid";
import DataGrid, { Column, DataGridRef } from "devextreme-react/data-grid";
import type { LTIPPlan } from "@/types/types";
import { GridSkeleton } from "@/components/Skeletons";
import { formatNumber } from "devextreme/localization";
import { useRef } from "react";
import { adjustSortArrowPosition, checkClientsBirthdates, getOwnerName } from "@/helper/helper";
import useHeaderChange from "@/hooks/useHeaderChange";
import { useLTIP } from "@/hooks/useLTIP";
import { LTIPType } from "@/types/enums";
import { useParams } from "react-router";
import HouseholdClientTabs from "@/components/HouseholdClientTabs";
import NoDataMessage from "@/components/NoDataMessage";
import useSelectedClient from "@/hooks/useSelectedClient";
import useDefaultSelectedClient from "@/hooks/useDefaultSelectedClient";
import Heading from "@/components/Heading";
import { NoContactBirthDateMessage } from "../HouseholdContact";

export default function SavingsRestorationPlan() {
  const dxGridRef = useRef<DataGridRef>(null);
  const { id } = useParams();
  const { household, upLoading } = useHeaderChange(id!, "Savings Restoration Plan");
  const { selectedClient, setSelectedClient } = useSelectedClient(household, LTIPType.SRP);
  const {
    data: srpData,
    isLoading: srpIsLoading,
    isError: srpIsError,
    isRefetching: srpIsRefetching,
  } = useLTIP(id!, LTIPType.SRP, selectedClient, household);

  useDefaultSelectedClient(household, srpData, selectedClient, setSelectedClient, LTIPType.SRP);

  const fa = srpData?.data[0];
  const ds = fa?.planYears;

  if (checkClientsBirthdates(household)) {
    return <NoContactBirthDateMessage household={household} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {upLoading ? (
        <div className="max-w-[16rem]">
          <GridSkeleton columns={2} rows={1} />
        </div>
      ) : household?.secondaryContact && srpData?.bothClientsHaveData ? (
        <HouseholdClientTabs
          household={household}
          selectedClientExternalCode={selectedClient}
          setSelectedClientExternalCode={setSelectedClient}
        />
      ) : null}
      {srpIsLoading || srpIsRefetching ? <SRPSkeleton /> : null}
      {!ds?.length && !srpIsLoading && !srpIsRefetching ? <NoDataMessage /> : null}
      {srpIsError ? <div>Failed fetching Savings Restoration Plan</div> : null}
      {ds?.length && !srpIsLoading && !srpIsRefetching ? (
        <>
          {!srpData?.bothClientsHaveData && !household?.isSingle ? (
            <Heading
              text={`Savings Restoration Plan for ${getOwnerName(household, selectedClient)}`}
            />
          ) : null}
          <DataGrid
            dataSource={ds ?? []}
            showBorders={true}
            showRowLines={true}
            ref={dxGridRef}
            showColumnLines={true}
            className="ao-grid"
            keyExpr={"externalCode"}
            wordWrapEnabled={true}
            onCellPrepared={(e) => {
              if (e.rowType === "header" || e.column.dataField === "planYear") {
                e.cellElement.style.backgroundColor = "var(--primary-color)";
                e.cellElement.style.color = "white";
                e.cellElement.style.fontWeight = "500";
              }
              adjustSortArrowPosition(dxGridRef);
            }}
          >
            <Column
              dataField="planYear"
              dataType="date"
              allowSorting={false}
              sortOrder={"asc"}
              allowEditing={false}
              width={160}
              alignment={"center"}
              headerCellRender={() => customHeader(`plan-year`, "plan year")}
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.year ? cellRenderer(cellData, "plan-year", item?.year) : "";
              }}
            />
            <Column
              dataField="contribution"
              dataType="number"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() => customHeader(`contribution`, "contribution")}
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.contribution
                  ? cellRenderer(
                      cellData,
                      "contribution",
                      formatNumber(item?.contribution / 100, "#%")
                    )
                  : "";
              }}
            />
            <Column
              dataField="distributionElection"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`distribution-election`, "distribution", "election")
              }
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.distributionElection
                  ? cellRenderer(cellData, "distribution-election", item?.distributionElection)
                  : "";
              }}
            />
          </DataGrid>
        </>
      ) : null}
    </div>
  );
}

export const SRPSkeleton = () => {
  return <GridSkeleton columns={3} rows={4} />;
};
